import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { ConfigProvider, Spin, App as AntdApp, Layout } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import { AuthProvider } from './hooks/useAuth'
import { useAuth } from './hooks/useAuthHook'
import Default from './pages/Default'
import Error from './pages/Error'
import Index from './pages/Index'
import Iden from './pages/Iden.jsx'
import IdenManagement from './pages/IdenMangement.jsx'
import Blog from './pages/Blog.jsx'
import CategoryManagement from './pages/CategoryManagement'
import AlbumManagement from './pages/AlbumMangement.jsx'
import ArticleManagement from './pages/ArticleMangement.jsx'
import BlogDetail from './pages/BlogDetail.jsx'
import BlogEditor from './pages/BlogEditor.jsx'
import Album from './pages/Album.jsx'
import PhotoManagement from './pages/PhotoMangement.jsx'
import Profile from './pages/Profile.jsx'
import ProfileEdit from './pages/ProfileEdit.jsx'
import Message from './pages/Message.jsx'
import Follow from './pages/Follow.jsx'
import Topbar from './components/Topbar'
import './App.css'
const { Content } = Layout

// 路由保护组件 - 用于管理页面
const ProtectedRoute = ({ children }) => {
    const { user } = useAuth()
    const { username } = useParams()

    // 未登录 - 跳转到登录页
    if (!user) {
        return <Navigate to="/iden/" replace />
    }

    // 登录用户名与路由用户名不匹配 - 跳转到登录用户的首页
    if (username && username !== user.username) {
        return <Navigate to={`/${user.username}/index/`} replace />
    }

    return children
}

// 主应用路由组件
function AppRoutes() {
    const { user, loading } = useAuth()

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" spinning={true} fullscreen={true} tip="加载中..." />
            </div>
        )
    }

    // 未登录状态 - 显示登录页面和公开页面
    if (!user) {
        return (
            <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
                <Topbar />
                <Content style={{ minHeight: 'calc(100vh - 64px)', marginTop: '64px' }}>
                    <Routes>
                        <Route path="/iden/" element={<Iden />} />
                        {/* 公开页面 - 未登录也可以访问 */}
                        <Route path="/" element={<Default />} />
                        <Route path="/:username/index/" element={<Index />} />
                        <Route path="/:username/album/" element={<Album />} />
                        <Route path="/:username/blog/" element={<Blog />} />
                        <Route path="/:username/blog/article/*" element={<BlogDetail />} />
                        <Route path="/:username/iden/profile/" element={<Profile />} />
                        <Route path="/:username/iden/message/" element={<Message />} />
                        <Route path="/:username/iden/follow/" element={<Follow />} />
                        <Route path="/:username/error" element={<Error />} />
                        <Route path="/error" element={<Error />} />
                        <Route path="*" element={<Navigate to="/error" replace />} />
                    </Routes>
                </Content>
            </Layout>
        )
    }

    // 已登录状态 - 显示管理系统界面
    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Topbar />
            <Content style={{ background: '#f5f5f5', minHeight: 'calc(100vh - 64px)', marginTop: '64px' }}>
                <Routes>
                    <Route path="/" element={<Default />} />
                    {/* 公开页面 - 不带 Management 的页面，任何用户都可以访问 */}
                    <Route path="/:username/index/" element={<Index />} />
                    <Route path="/:username/album/" element={<Album />} />
                    <Route path="/:username/blog/" element={<Blog />} />
                    <Route path="/:username/blog/article/*" element={<BlogDetail />} />
                    <Route path="/:username/iden/profile/" element={<Profile />} />
                    <Route path="/:username/iden/message/" element={<Message />} />
                    <Route path="/:username/iden/follow/" element={<Follow />} />
                    <Route path="/:username/error" element={<Error />} />
                    <Route path="/error" element={<Error />} />
                    {/* 管理页面 - 带 Management 的页面，需要登录且用户名匹配 */}
                    <Route
                        path="/:username/album/new/*"
                        element={
                            <ProtectedRoute>
                                <AlbumManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/:username/album/edit/*"
                        element={
                            <ProtectedRoute>
                                <AlbumManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/:username/album/upload/*"
                        element={
                            <ProtectedRoute>
                                <PhotoManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/:username/album/photo/edit/*"
                        element={
                            <ProtectedRoute>
                                <PhotoManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/:username/blog/new/*"
                        element={
                            <ProtectedRoute>
                                <CategoryManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/:username/blog/edit/*"
                        element={
                            <ProtectedRoute>
                                <CategoryManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/:username/blog/article/new/*"
                        element={
                            <ProtectedRoute>
                                <BlogEditor />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/:username/blog/article/edit/*"
                        element={
                            <ProtectedRoute>
                                <BlogEditor />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/:username/iden/profile/edit/*"
                        element={
                            <ProtectedRoute>
                                <ProfileEdit />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/:username/iden/manage/*"
                        element={
                            <ProtectedRoute>
                                <IdenManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to={`/${user?.username ?? 'error'}/index/`} replace />} />
                    </Routes>
            </Content>
        </Layout>
    )
}

// 主应用组件
export function App() {
    return (
        <ConfigProvider locale={zhCN}>
            <AntdApp>
                <AuthProvider>
                    <Router>
                        <AppRoutes />
                    </Router>
                </AuthProvider>
            </AntdApp>
        </ConfigProvider>
    )
}

export default App
